import React, { useCallback, useEffect, useMemo } from 'react';

import { ClusterControllerProps } from './MarkerTypes';
import ClusterMarker from './ClusterMarker';
import FeatureMarker from './FeatureMarker';
import { markersToGeoJSON, getValidMarkerType } from './MarkerUtils';
import { useSupercluster } from './hooks/useSupercluster';
import { useMapView } from './hooks/useMapView';
import { isValidBounds, isValidZoom } from './utils/validation';
import type { ClusterOrFeature, GeoJSONFeature } from './utils/types';

// Cluster renderer component
const ClusterRenderer: React.FC<{
  feature: ClusterOrFeature;
  index: number;
  handleClusterClick: (marker: google.maps.marker.AdvancedMarkerElement, clusterId?: number) => void;
  handleMarkerClick: (marker: google.maps.marker.AdvancedMarkerElement, featureId: string) => void;
  getLeaves: (clusterId: number, limit?: number) => GeoJSONFeature[];
}> = ({ feature, handleClusterClick, handleMarkerClick, getLeaves }) => {
  const position = {
    lat: feature.geometry.coordinates[1] ?? 0,
    lng: feature.geometry.coordinates[0] ?? 0
  };

  if (feature.properties.cluster) {
    // Validate cluster ID before calling getLeaves
    if (typeof feature.id !== 'number' || isNaN(feature.id)) {
      console.warn('Invalid cluster ID:', feature.id);
      return null;
    }

    const leaves = getLeaves(feature.id, 10000); // Get all leaves, not just the default limit

    // Additional validation - if getLeaves returns empty array, it might be an invalid cluster
    if (!leaves || leaves.length === 0) {
      console.warn(`No leaves found for cluster ${feature.id}, skipping render`);
      return null;
    }

    return <ClusterMarker clusterId={feature.id} position={position} features={leaves} onMarkerClick={handleClusterClick} />;
  }

  return (
    <FeatureMarker
      featureId={String(feature.id ?? '')}
      position={position}
      type={getValidMarkerType(feature.properties.type)}
      label={feature.properties.label}
      onMarkerClick={handleMarkerClick}
    />
  );
};

const ClusterController: React.FC<ClusterControllerProps> = React.memo(
  ({ markers, onClusterClick, onMarkerClick, onNumClustersChange, clusteringOptions }) => {
    const { mapInstance, bounds, zoom } = useMapView();
    const { isSuperclusterLoaded, featuresLoaded, loadFeatures, getClusters, getLeaves, getClusterExpansionZoom } =
      useSupercluster(clusteringOptions);

    // Convert markers to GeoJSON
    const geojson = useMemo(() => {
      return markersToGeoJSON(markers);
    }, [markers]);

    // Load data into supercluster
    useEffect(() => {
      loadFeatures(geojson.features as unknown as Parameters<typeof loadFeatures>[0]);
    }, [geojson.features, loadFeatures]);

    // Get clusters for current view
    const clusters = useMemo(() => {
      if (geojson.features.length === 0 || !isSuperclusterLoaded || !featuresLoaded) {
        return [];
      }

      if (!isValidBounds(bounds)) {
        return [];
      }

      if (!isValidZoom(zoom)) {
        return [];
      }

      try {
        const clusters = getClusters(bounds, zoom);

        // Simplified validation - only filter out truly invalid clusters
        // Most validation is already done in getClusters
        return clusters.filter((cluster) => {
          if (!cluster?.id) return false;
          // For clusters, ID must be numeric; for markers, ID can be string or number
          if (cluster.properties?.cluster) {
            return typeof cluster.id === 'number' && !isNaN(cluster.id);
          }
          return true;
        });
      } catch (error) {
        console.error('Error in clusters:', error);
        return [];
      }
    }, [bounds, zoom, isSuperclusterLoaded, featuresLoaded, getClusters, geojson.features]);

    // Update cluster count
    useEffect(() => {
      onNumClustersChange?.(clusters.length);
    }, [clusters.length, onNumClustersChange]);

    // Handle cluster click
    const handleClusterClick = useCallback(
      (marker: google.maps.marker.AdvancedMarkerElement, clusterId?: number) => {
        if (!clusterId || !mapInstance) return;

        try {
          const expansionZoom = getClusterExpansionZoom(clusterId);
          const position = marker.position as google.maps.LatLngLiteral;

          if (position && expansionZoom > 0) {
            mapInstance.panTo(position);
            mapInstance.setZoom(expansionZoom);
          }

          if (onClusterClick) {
            const leaves = getLeaves(clusterId);
            onClusterClick(marker, leaves);
          }
        } catch (error) {
          console.error('Error handling cluster click:', error);
        }
      },
      [mapInstance, onClusterClick, getClusterExpansionZoom, getLeaves]
    );

    // Handle individual marker click
    const handleMarkerClick = useCallback(
      (marker: google.maps.marker.AdvancedMarkerElement, featureId: string) => {
        if (onMarkerClick) {
          onMarkerClick(marker, featureId);
        }
      },
      [onMarkerClick]
    );

    // Render clusters and markers
    const renderedItems = clusters.map((feature, index) => {
      // Create a more stable key that includes cluster properties
      const keySuffix = feature.properties?.cluster
        ? `cluster-${feature.id}-${feature.properties.point_count || 0}`
        : `marker-${feature.id}`;

      return (
        <ClusterRenderer
          key={`${keySuffix}-${index}`}
          feature={feature}
          index={index}
          handleClusterClick={handleClusterClick}
          handleMarkerClick={handleMarkerClick}
          getLeaves={getLeaves}
        />
      );
    });

    return <>{renderedItems}</>;
  }
);

ClusterController.displayName = 'ClusterController';

export default ClusterController;
